import React, { useEffect, useRef, useState } from "react";
import { PageHeader } from "@/components/Layout";
import { api, fmtDuration, fmtTime, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Clock, Play, Stop, Briefcase } from "@phosphor-icons/react";
import { toast } from "sonner";

function liveElapsed(startIso) {
  const secs = Math.max(0, Math.floor((Date.now() - new Date(startIso).getTime()) / 1000));
  const h = String(Math.floor(secs / 3600)).padStart(2, "0");
  const m = String(Math.floor((secs % 3600) / 60)).padStart(2, "0");
  const s = String(secs % 60).padStart(2, "0");
  return `${h}:${m}:${s}`;
}

export default function TimeClock() {
  const [active, setActive] = useState(null);
  const [entries, setEntries] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState("none");
  const [notes, setNotes] = useState("");
  const [tick, setTick] = useState(0);
  const [busy, setBusy] = useState(false);
  const timer = useRef();

  const load = async () => {
    const [a, my, js] = await Promise.all([
      api.timeActive().catch(() => null),
      api.myTime().catch(() => []),
      api.listJobs({}).catch(() => []),
    ]);
    setActive(a);
    setEntries(my);
    setJobs(js);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (active) {
      timer.current = setInterval(() => setTick((t) => t + 1), 1000);
      return () => clearInterval(timer.current);
    }
  }, [active]);

  const doClockIn = async () => {
    setBusy(true);
    try {
      await api.clockIn({ job_id: jobId === "none" ? null : jobId, notes });
      toast.success("Clocked in — timer running");
      setNotes(""); setJobId("none");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const doClockOut = async () => {
    setBusy(true);
    try {
      await api.clockOut({});
      toast.success("Clocked out — shift saved");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const todayMins = entries
    .filter((e) => e.clock_in && new Date(e.clock_in).toDateString() === new Date().toDateString())
    .reduce((s, e) => s + (e.duration_minutes || 0), 0);

  return (
    <div className="p-8 max-w-[1100px]">
      <PageHeader title="Time Clock" subtitle="Clock in when you start, clock out when you're done" />

      {/* Clock card */}
      <div className={`rounded-xl p-8 mb-6 text-white ${active ? "bg-[#0B1726]" : "bg-[#0B1726]"} relative overflow-hidden`}>
        <div className="relative z-10">
          {active ? (
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <div className="flex items-center gap-2 text-[#00B4D8] text-xs font-bold uppercase tracking-[0.2em] mb-2">
                  <span className="w-2 h-2 rounded-full bg-[#00B4D8] animate-pulse" /> On the clock
                </div>
                <div className="font-display text-5xl font-extrabold tabular-nums tracking-tight" data-testid="live-timer">
                  {liveElapsed(active.clock_in)}
                </div>
                <div className="text-slate-400 text-sm mt-2">
                  Started {fmtTime(active.clock_in)}
                  {active.job_number && <span className="ml-2 text-slate-300">· {active.job_number} {active.job_title}</span>}
                </div>
              </div>
              <Button onClick={doClockOut} disabled={busy} data-testid="clock-out-button" className="h-14 px-8 bg-orange-500 hover:bg-orange-600 text-base font-bold">
                <Stop size={22} weight="fill" className="mr-2" /> Clock Out
              </Button>
            </div>
          ) : (
            <div className="flex flex-col lg:flex-row lg:items-end gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-2 text-slate-400 text-xs font-bold uppercase tracking-[0.2em] mb-3">
                  <Clock size={16} weight="duotone" /> Ready to start
                </div>
                <div className="grid sm:grid-cols-2 gap-4 max-w-xl">
                  <div>
                    <Label className="text-slate-300">Job (optional)</Label>
                    <Select value={jobId} onValueChange={setJobId}>
                      <SelectTrigger className="mt-1.5 bg-white/10 border-white/20 text-white" data-testid="clock-job-select"><SelectValue placeholder="No specific job" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No specific job</SelectItem>
                        {jobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.number} — {j.title}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-300">Note (optional)</Label>
                    <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Driveway job on Elm St" className="mt-1.5 bg-white/10 border-white/20 text-white placeholder:text-slate-500 h-10 min-h-0 py-2" data-testid="clock-notes" />
                  </div>
                </div>
              </div>
              <Button onClick={doClockIn} disabled={busy} data-testid="clock-in-button" className="h-14 px-8 bg-[#00B4D8] hover:bg-cyan-600 text-base font-bold">
                <Play size={22} weight="fill" className="mr-2" /> Clock In
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Today total */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500">Hours Today</div>
          <div className="font-display text-3xl font-extrabold text-slate-900 mt-2">{fmtDuration(todayMins)}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500">Shifts Logged</div>
          <div className="font-display text-3xl font-extrabold text-slate-900 mt-2">{entries.length}</div>
        </div>
      </div>

      {/* History */}
      <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">Recent Shifts</h3>
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
        <Table>
          <TableHeader><TableRow className="bg-slate-50">
            <TableHead>Clock In</TableHead><TableHead>Clock Out</TableHead><TableHead>Duration</TableHead><TableHead>Job</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {entries.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-10">No shifts yet. Clock in to get started.</TableCell></TableRow>}
            {entries.map((e) => (
              <TableRow key={e.id} className="hover:bg-slate-50" data-testid={`my-entry-${e.id}`}>
                <TableCell className="text-sm">{fmtDateTime(e.clock_in)}</TableCell>
                <TableCell className="text-sm">{e.clock_out ? fmtDateTime(e.clock_out) : <span className="text-[#00B4D8] font-semibold">Active…</span>}</TableCell>
                <TableCell className="font-semibold">{fmtDuration(e.duration_minutes)}</TableCell>
                <TableCell className="text-sm text-slate-500">{e.job_number ? <span className="inline-flex items-center gap-1"><Briefcase size={13} />{e.job_number}</span> : "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
